import * as React from 'react';
import Icon from '@cx-web/components/primitives/icon';
import { useRepubContext } from '@cx-web/feature-repub';
import { removeModalSearchParamsFromUrl } from '@cx-web/utils/us-utils';

import { REPUBS } from '@cx-modules/constants';

import { ModalView, useModalContext } from '~/providers/modal-context';
import { useMyOCAuth } from '~/providers/myoc-auth';

import { AccountPersonalisation } from './shared/AccountPersonalisation';
import styles from './style/legacy-auth-modal.module.css';

const myOCLoginModals = ['LoginForm', 'LoginVerificationForm', 'SignupForm'];

export interface IProps {
  baseUrl?: string;
  children?: React.ReactNode;
  hideHeader?: boolean;
  initView?: ModalView;
}

export const LegacyAuthModal: React.FC<IProps> = ({ baseUrl = '', children, hideHeader, initView }) => {
  const legacyLoginSuccessRef = React.useRef(false);

  const { repub } = useRepubContext();
  const { myocData } = useMyOCAuth();

  const [{ loading, ocAuthReady, refreshPage, showModal, view }, dispatch] = useModalContext();

  // Set ocAuthReady from window
  React.useEffect(() => {
    if (!dispatch) return;

    window.addEventListener('OC:ocAuthReady', () => {
      dispatch({ type: 'SET_OC_AUTH_READY' });
    });
  }, [dispatch]);

  // Set view, showModal and loading
  React.useEffect(() => {
    const modalView = view || initView;

    const canShowView = !!modalView;
    const canShowChildren = typeof children !== 'undefined';
    const canDisableLoading = !(canShowView || canShowChildren);

    dispatch({
      type: 'SET_SHOW_MODAL_VIEW_AND_LOADING',
      payload: {
        // @ts-ignore
        view: modalView,
        showModal: canShowView || canShowChildren,
        loading: canDisableLoading,
      },
    });
  }, [view, initView, children, dispatch]);

  // Reload the page
  React.useEffect(() => {
    if (!refreshPage) return;

    window.location.reload();
  }, [refreshPage]);

  // Subscribe to login success event [legacy modal only]
  React.useEffect(() => {
    const onLoginSuccess = () => (legacyLoginSuccessRef.current = true);
    window.addEventListener('OC:LoggedIn', onLoginSuccess);

    return () => {
      window.removeEventListener('OC:LoggedIn', onLoginSuccess);
    };
  }, []);

  // Trigger personalisation flow [US only]
  React.useEffect(() => {
    if (repub !== REPUBS.US.name) return;

    const shouldOnboardFollowingLegacyLogin =
      legacyLoginSuccessRef.current && !!myocData.myOCUser && !myocData.myOCUser.personalisation;

    if (shouldOnboardFollowingLegacyLogin) {
      dispatch({
        type: 'SET_VIEW',
        // @ts-ignore
        payload: { view: ModalView.AccountPersonalisation },
      });
    }
  }, [repub, myocData, dispatch]);

  // Invoke window.ocAuth.openAtView methods
  React.useEffect(() => {
    removeModalSearchParamsFromUrl();

    if (!ocAuthReady) return;

    switch (view) {
      case ModalView.LoginForm:
        window.ocAuth.openAtView('LOGIN');
        break;

      case ModalView.LoginVerificationForm:
        window.ocAuth.openAtView('LOGIN_FROM_EMAIL_VERIFICATION');
        break;

      case ModalView.SignupForm:
        window.ocAuth.openAtView('SIGN_UP');
        break;

      default:
        break;
    }
  }, [view, ocAuthReady]);

  // NOTE: we require the FE flag - showModal - in order that
  // we can conditionally render modals according to user events

  const hasChildren = !!children;
  const viewHasModal = (view || '') in ModalView;

  const viewIsMyOCLoginModal = myOCLoginModals.includes(view as string);

  const shouldHideModal = (() => {
    switch (true) {
      // simple cases
      case loading:
      case !showModal:
      case !viewHasModal && !hasChildren:
        return true;

      // complex cases
      case !ocAuthReady && viewIsMyOCLoginModal:
        return true;

      default:
        return false;
    }
  })();

  const shouldHideHeader = (() => {
    switch (true) {
      case hideHeader:
      case viewIsMyOCLoginModal:
        return true;

      default:
        return false;
    }
  })();

  const closeModal = (): void => {
    removeModalSearchParamsFromUrl();
    window.ocAuth.openAtView('Close');
    dispatch({ type: 'HIDE_MODAL' });
  };

  const closeModalAndRefreshPage = (): void => {
    removeModalSearchParamsFromUrl();
    window.ocAuth.openAtView('Close');
    dispatch({ type: 'HIDE_MODAL_AND_REFRESH_PAGE' });
  };

  // Add modal helper methods to window
  React.useEffect(() => {
    if (!dispatch) return;

    window.openModalAtView = (modalView: ModalView): void => {
      dispatch({
        type: 'SET_VIEW',
        // @ts-ignore
        payload: {
          view: modalView,
        },
      });
    };

    window.closeMyOCLoginModal = (): void => {
      removeModalSearchParamsFromUrl();
      window.ocAuth.openAtView('Close');
    };

    window.closeAllModals = (): void => {
      removeModalSearchParamsFromUrl();
      window.ocAuth.openAtView('Close');
      dispatch({ type: 'HIDE_MODAL' });
    };
  }, [dispatch]);

  const renderHeader = (): JSX.Element | null => {
    if (shouldHideHeader) return null;

    const headerCloseIconClickHandler = (): void => {
      switch (view) {
        default:
          closeModal();
          break;
      }
    };

    const renderHelpIcon = (): JSX.Element => {
      // NOTE: we do not currently have any views that
      // require us to render help icon on cx-web
      return <span />;
    };

    const renderCloseIcon = (): JSX.Element => {
      return (
        <span className={styles.closeIconWrapper} data-testid="close-modal" onClick={headerCloseIconClickHandler}>
          <Icon className={styles.closeIcon} height={24} icon="Close" width={26} />
        </span>
      );
    };

    return (
      <div className={styles.modalHeader} data-testid="modal-header">
        {renderHelpIcon()}
        {renderCloseIcon()}
      </div>
    );
  };

  const renderViewOrChildren = (): JSX.Element | JSX.Element[] | null | undefined => {
    // 1. Render Views
    switch (view) {
      case ModalView.AccountPersonalisation:
        return <AccountPersonalisation baseUrl={baseUrl} closeModal={closeModal} shouldRenderHeadless={true} />;

      default:
        break;
    }

    // 2. Render Children
    return React.Children.map(children, child => {
      const item = child as React.ReactElement;

      // Return item if regular DOM element
      if (typeof item.type === 'string') {
        return item;
      }

      // Return compound component
      return React.cloneElement(item, {
        closeModal,
        closeModalAndRefreshPage,
      });
    });
  };

  const renderLoginModal = (): JSX.Element => {
    return <div id="myoc-login-modal"></div>;
  };

  return (
    <div className={[styles.modalBackground, shouldHideModal ? styles.hidden : ''].join(' ')}>
      <div className={styles.modalContainer} data-testid="myoc-modal-container">
        {renderHeader()}
        {renderViewOrChildren()}
        {renderLoginModal()}
      </div>
    </div>
  );
};

LegacyAuthModal.displayName = 'LegacyAuthModal[FormerlyPremiumModal]';
